import { Input, InputProps } from '@heroui/react';
import { ReactNode, useState } from 'react';
import { IoEyeOffOutline, IoEyeOutline } from 'react-icons/io5';

interface CustomInputProps extends InputProps {
  id?: string;
  label?: string;
  name?: string;
  type: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'date';
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger';
  variant?: 'flat' | 'bordered' | 'underlined' | 'faded';
  labelPlacement?: 'inside' | 'outside' | 'outside-left';
  errorMessage?: string;
  description?: string;
  defaultValue?: string;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  minLength?: number;
  maxLength?: number;
  endContent?: ReactNode;
  startContent?: ReactNode;
  onValueChange?: (value: string) => void;
  value?: string;
  className?: string;
}

const CustomInput = ({
  id,
  label = '',
  placeholder = 'Enter placeholder',
  size = 'sm',
  radius = 'sm',
  type = 'text',
  color = 'default',
  variant = 'bordered',
  labelPlacement = 'outside',
  description = '',
  errorMessage = '',
  isDisabled = false,
  isInvalid = false,
  minLength,
  maxLength,
  defaultValue = '',
  name = '',
  startContent,
  endContent,
  onValueChange,
  value,
  className,

  ...props
}: CustomInputProps) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const isPasswordField = type === 'password';
  const resolvedType = isPasswordField && isPasswordVisible ? 'text' : type;

  const passwordToggle = isPasswordField ? (
    <button
      type="button"
      aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
      className="text-default-400 hover:text-default-600 focus:outline-none"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => setIsPasswordVisible((prev) => !prev)}
    >
      {isPasswordVisible ? (
        <IoEyeOffOutline size={18} />
      ) : (
        <IoEyeOutline size={18} />
      )}
    </button>
  ) : null;

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-xs text-gray-600 font-medium">
          {label}
          {props.isRequired && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <Input
        id={id}
        type={resolvedType}
        name={name}
        placeholder={placeholder}
        size={size}
        radius={radius}
        color={color}
        variant={variant}
        defaultValue={defaultValue}
        isDisabled={isDisabled}
        description={description}
        errorMessage={errorMessage}
        isInvalid={isInvalid}
        value={value}
        onValueChange={onValueChange}
        startContent={startContent}
        endContent={endContent ?? passwordToggle}
        className={`bg-white"  ${className}`}
        minLength={minLength}
        maxLength={maxLength}
        classNames={{
          input: `placeholder:text-gray-400 placeholder:text-xs text-xs bg-white${
            isPasswordField ? ' hide-native-password-reveal' : ''
          }`,
          description: 'text-[11px]',
          inputWrapper: 'border border-gray-300 text-xs',
          errorMessage: 'text-[11px] text-red-500 ',
          helperWrapper: 'text-xs text-gray-500',
        }}
        {...props}
      />
    </div>
  );
};

export default CustomInput;
